import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import styles from './Chart.module.css';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ReferenceArea, ResponsiveContainer } from 'recharts';
import { StepsIcon } from '../../../components/icons/Icons';
import { useSelector } from 'react-redux';
import { getGraphXValue } from '../../../utils/dateUtils';
import PerinTooltip from './components/PerinTooltip';
import { sortData } from '../../../utils/graphUtils';
import { getVitalUnit, convertMeasurementValue } from '../../../utils/unitUtils';
import VitalCardHeader from './components/VitalCardHeader';

const StepCardView = React.forwardRef(({ className, healthEvaluationData, vitalName, handlePointClick, alertsForPeriod }, ref) => {
  const chartContainerRef = useRef(null);
  const userUnitPreference = useSelector((state) => state.userInfo.unitType);
  const { startDate, endDate, averageGrouped, readingInterval } = useSelector((state) => state.patientDetailFilter);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({});
  const vitals = useSelector((state) => state.vital.modalities[vitalName]);
  const [opacity, setOpacity] = useState({});
  const dateFormat = useSelector((state) => state.userInfo.dateFormat);

  const updateData = useCallback(() => {
    // Group data by date and deviceName
    const groupedData = healthEvaluationData.reduce((acc, entry) => {
      const date = entry.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][entry.deviceName] = entry;
      return acc;
    }, {});

    let transformedData = Object.values(groupedData);
    transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    setData(transformedData);
    const deviceOpacity = healthEvaluationData.reduce((acc, entry) => {
      const { deviceName } = entry;

      if (!acc[deviceName]) {
        acc[deviceName] = [];
      }
      acc[deviceName].push(entry);

      return acc;
    }, {});

    setOpacity(Object.keys(deviceOpacity).reduce((acc, key) => {
      acc[key] = 1;
      return acc;
    }, {}));
    setDisplayData(getLatestMeasurementData(healthEvaluationData));
  }, [healthEvaluationData, vitalName]);

  useEffect(() => {
    updateData();
  }, [updateData, startDate, endDate]);

  const handleLegendClick = (e) => {
    const deviceName = e.value;
    setOpacity((op) => ({ ...op, [deviceName]: op[deviceName] === 1 ? 0 : 1 }));
  };


  const getLatestMeasurementData = useCallback((healthEvaluationData) => {
    if (!healthEvaluationData || healthEvaluationData.length === 0) {
      return { measurement: 'No Data', latestDate: 'N/A' };
    }
    const unit = getVitalUnit(vitals?.modalityId, userUnitPreference);
    const deviceNamesData = healthEvaluationData.filter(entry => entry[vitalName] !== null);
    if (deviceNamesData.length === 0) {
      return { measurement: 'No Data', latestDate: 'N/A' };
    }
    const validEntries = deviceNamesData.filter(entry =>
      entry[vitalName] !== null && !isNaN(entry[vitalName])
    );
    if (averageGrouped) {

      if (validEntries.length === 0) {
        return {
          measurement: 'No Data',
          latestDate: 'Interval Average',
        };
      }
      const total = validEntries.reduce((sum, entry) => sum + entry[vitalName], 0);
      const average = total / validEntries.length;
      // Calculate out of threshold count
      let outOfRangeCount = 0;
      if ((vitals?.upperLimit !== undefined && vitals?.upperLimit !== 0) || (vitals?.lowerLimit !== undefined && vitals?.lowerLimit !== 0)) {
        const outOfRange = validEntries.filter((entry) => {
          const value = entry[vitalName];
          if (value === null || value === undefined) {
            return false;
          }

          const convertedUpperLimit = convertMeasurementValue(vitals?.upperLimit, vitals?.modalityId, userUnitPreference);
          const convertedLowerLimit = convertMeasurementValue(vitals?.lowerLimit, vitals?.modalityId, userUnitPreference);
          if (value > convertedUpperLimit || value < convertedLowerLimit) {
            return true;
          }
          return false;
        });
        outOfRangeCount = outOfRange.length;
      }

      // Format the value
      let formattedValue = Math.round(average);

      // Build latestDate string with threshold count if applicable
      let latestDate = 'Interval Average';
      if (outOfRangeCount > 0) {
        latestDate = `${outOfRangeCount} out of threshold`;
      }
      return {
        measurement: `${formattedValue}${unit}`,
        latestDate: latestDate,
      };
    } else {
      const total = validEntries.reduce((sum, entry) => sum + entry[vitalName], 0);
      let outOfRangeCount = 0;
      if ((vitals?.upperLimit !== undefined && vitals?.upperLimit !== 0) || (vitals?.lowerLimit !== undefined && vitals?.lowerLimit !== 0)) {
        const outOfRange = deviceNamesData.filter((entry) => {
          const value = entry[vitalName];
          if (value === null || value === undefined) {
            return false;
          }

          const convertedUpperLimit = convertMeasurementValue(vitals?.upperLimit, vitals?.modalityId, userUnitPreference);
          const convertedLowerLimit = convertMeasurementValue(vitals?.lowerLimit, vitals?.modalityId, userUnitPreference);
          if (value > convertedUpperLimit || value < convertedLowerLimit) {
            return true;
          }
        });
        outOfRangeCount = outOfRange.length;
      }
      // use total value for step count
      let formattedValue = Math.round(total);
      const latestMeasurement = deviceNamesData.reduce((latest, entry) => {
        const dateA = new Date(latest.date);
        const dateB = new Date(entry.date);
        return dateB > dateA ? entry : latest;
      }, deviceNamesData[0]);

      if (!latestMeasurement) {
        return { measurement: 'No Data', latestDate: 'N/A' };
      }

      const value = latestMeasurement[vitalName];
      if (value === null || value === undefined) {
        return { measurement: 'No Data', latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A') };
      }
      return {
        measurement: `${formattedValue}${unit}`,
        latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A'),
        outOfRangeCount: outOfRangeCount
      };
    }

  }, [averageGrouped, vitalName]);

  const deviceNames = Array.from(new Set(healthEvaluationData.map(entry => entry.deviceName)));

  const deviceNamesData = deviceNames.filter(deviceName =>
    healthEvaluationData.some(entry => entry.deviceName === deviceName && entry.steps !== null)
  );

  const getHighestMeasurement = () => {
    let highest = 0;
    deviceNames.forEach((deviceName) => {
      data.forEach(entry => {
        if (entry[deviceName] && entry[deviceName][vitalName] !== null) {
          if (highest === null || entry[deviceName][vitalName] > highest) {
            highest = entry[deviceName][vitalName];
          }
        }
      });
    })
    return highest;
  };

  const colors = {
    0: '#7A6B78',
    1: '#00162E',
  };

  const calculateDomain = () => {
    let maxDomain = vitals?.upperLimit;
    let highestMeasurement = getHighestMeasurement();

    if (highestMeasurement !== null && highestMeasurement !== 0 && highestMeasurement > maxDomain) {
      maxDomain = highestMeasurement;
    }

    const buffer = maxDomain * 0.2;

    return [0, maxDomain + buffer];
  };

  const generateTicks = () => {
    let ticks = calculateDomain();

    if (vitals?.lowerLimit !== 0) {
      ticks.push(vitals?.lowerLimit)
    }
    if (vitals?.targetValue !== 0) {
      ticks.push(vitals?.targetValue)
    }
    if (vitals?.upperLimit !== 0) {
      ticks.push(vitals?.upperLimit)
    }

    const uniqueTicks = Array.from(new Set(ticks.filter(tick => tick !== undefined && tick !== null))).sort((a, b) => a - b);
    return uniqueTicks;
  };

  const domain = calculateDomain();


  const sortedDataWithAlerts = useMemo(() => {
    const alertsForModality = alertsForPeriod.filter(
      (alert) => alert.alert_modalityId === vitals?.modalityId || alert.alert_subType === 'fall status'
    );

    const alertData = alertsForModality.map(alert => ({
      date: getGraphXValue(alert.createdDate, dateFormat, averageGrouped, readingInterval),
      alertY: domain[1],
      alert: alert
    }));

    let updatedData = [...data];

    alertData.forEach(alertItem => {
      const matchIndex = updatedData.findIndex(item => item.date === alertItem.date);
      if (matchIndex !== -1) {
        updatedData[matchIndex] = { ...updatedData[matchIndex], alertY: alertItem.alertY, alert: alertItem.alert };
      } else {
        updatedData.push(alertItem);
      }
    });

    updatedData.sort(sortData);

    return updatedData;
  }, [data, alertsForPeriod, vitals?.modalityId]);

  return (
    <div className={`${styles.cardContainer} ${className}`} ref={chartContainerRef}>
      <VitalCardHeader
        Icon={StepsIcon}
        vitalName={vitals.modalityName}
        measurement={displayData.measurement}
        latestDate={displayData.latestDate}
        outOfRangeCount={displayData.outOfRangeCount}
      />
      <div className={styles.chartWrapper} ref={ref}>
        <ResponsiveContainer width={"100%"} height={250}>
          <BarChart data={sortedDataWithAlerts} margin={{ bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" fontSize={12} />
            <YAxis
              domain={domain}
              ticks={generateTicks()}
              tickCount={6}
              fontSize={12}
            />
            <Tooltip contentStyle={{ backgroundColor: 'white' }} content={<PerinTooltip vitals={vitals} />} />
            <Legend
              verticalAlign="top"
              height={30}
              iconType="square"
              align={"right"}
              wrapperStyle={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}
              payload={
                deviceNamesData.map((deviceName, index) => ({
                  value: deviceName,
                  type: 'square',
                  color: colors[index] || '#000000',
                }))}
              onClick={handleLegendClick}
            />
            {vitals?.upperLimit && vitals?.upperLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.upperLimit} stroke="red" strokeDasharray="3 3" />
                <ReferenceArea y1={domain[1]} y2={vitals?.upperLimit} fill="rgba(235, 182, 182, 0.12)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.lowerLimit && vitals?.lowerLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.lowerLimit} stroke="red" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.lowerLimit} y2={domain[0]} fill="rgba(235, 182, 182, 0.12)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {deviceNamesData.map((deviceName, index) => (
              <Bar
                key={deviceName}
                dataKey={deviceName + '.steps'}
                fill={opacity[deviceName] === 1 ? `rgba(73, 108, 163, ${opacity[deviceName]})` : 'none'}
                strokeOpacity={opacity[deviceName] || 0}
                onClick={(e) => handlePointClick(e, deviceName, vitals)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default StepCardView;
